import React, { type MouseEvent } from 'react';
import DOMPurify from 'dompurify';
import { Text } from '@atlaskit/primitives';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';

import { useRouterActions, usePathParam } from '@atlassian/react-resource-router';

import { toReportId } from '../../../../../common/model/index.tsx';
import { getReportsFragment } from '../../../../../common/navigation/index.tsx';
import ArticleProfile from './article-profile/index.tsx';
import AgentField from './fields/agent-cell/index.tsx';
import Ratings from './fields/ratings/index.tsx';
import { RequestStatus } from './fields/request-status/index.tsx';
import KbLink from './kb-link/index.tsx';
import { getKbLinkProps, getAgentFieldProps } from './utils/index.tsx';

type Props = {
	fieldHtml: string;
	fieldClass?: string;
};
/**
     Hacky way to transform Reports response to React components.
     Ideally we should change the response but it involves too much work.
 */
const FieldRenderer = ({ fieldHtml, fieldClass }: Props) => {
	const [paramReportId] = usePathParam('reportId');
	const [paramTimescaleId] = usePathParam('timescaleId');

	const { push } = useRouterActions();

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = fieldHtml;

	if (tempDiv && tempDiv.firstChild) {
		if (tempDiv.querySelector('SD-KB-DIALOG-LINK')) {
			const el = tempDiv.firstChild;

			// satisfy flow
			if (el instanceof Element) {
				const kbLinkProps = getKbLinkProps(el);
				return <KbLink {...kbLinkProps} />;
			}
		}

		if (fieldClass === 'reporter' || fieldClass === 'assignee') {
			const agentFieldProps = getAgentFieldProps(tempDiv);
			if (agentFieldProps !== undefined) {
				return <AgentField agent={{ ...agentFieldProps }} />;
			}
		}

		if (tempDiv.querySelector('.sd-feedback-rating-column')) {
			const rating = tempDiv.querySelectorAll('.sd-feedback-rating-column .feedback-star').length;
			return Ratings[rating];
		}

		if (tempDiv.querySelector('.report-drilldown-field-view-article-user')) {
			const nameSpan = tempDiv.querySelector('.drilldown-field-username');
			const image = tempDiv.querySelector('img');

			if (nameSpan instanceof Element && image instanceof Element) {
				const name = nameSpan.innerHTML;
				const imageSrc = String(image.getAttribute('src'));
				return <ArticleProfile name={name} imageUrl={imageSrc} withBorder />;
			}
		}

		if (tempDiv.querySelector('.sd-request-type')) {
			const image = tempDiv.querySelector('img');

			if (image instanceof Element) {
				const name = tempDiv.innerText || '';
				const imageSrc = String(image.getAttribute('src'));
				return <ArticleProfile name={name} imageUrl={imageSrc} />;
			}
		}

		if (fieldClass === 'status') {
			return <RequestStatus source={tempDiv.children[0]} />;
		}

		if (fieldClass === 'issuekey' || fieldClass === 'summary') {
			const reportId = toReportId(String(paramReportId));
			const timescaleId = paramTimescaleId !== undefined ? Number(paramTimescaleId) : undefined;
			const link =
				tempDiv.querySelector('a[data-issue-key]') || tempDiv.querySelector('a[data-jira-key]');

			if (!link) {
				fireErrorAnalytics({
					meta: {
						id: 'fieldRendererPayloadAssumptions',
						packageName: 'jiraServicedeskReportsCommon',
						teamName: 'itsm-saga',
					},
				});
				return <div>{fieldHtml}</div>;
			}

			// @ts-expect-error - TS2339 - Property 'dataset' does not exist on type 'Element'. | TS2339 - Property 'dataset' does not exist on type 'Element'.
			const issueKey = link.dataset.issueKey || link.dataset.jiraKey;
			const onClick = (event: MouseEvent<HTMLElement>) => {
				if (!reportId) return;

				const { ctrlKey, metaKey } = event;
				// Let default browser behaviour occur when opening link in new tab/window
				// metaKey for macOS cmd. ctrKey for other OSes.
				if (ctrlKey || metaKey) {
					return;
				}
				if (issueKey) {
					const path = getReportsFragment(reportId, toIssueKey(issueKey), timescaleId);

					event.preventDefault();

					push(path);
				}
			};

			return (
				<div>
					{}
					<Text as="p">
						<a
							// @ts-expect-error - TS2339 - Property 'dataset' does not exist on type 'Element'.
							data-issue-key={link.dataset.issueKey}
							href={link?.attributes?.getNamedItem('href')?.value}
							onClick={onClick}
						>
							{link.textContent}
						</a>
					</Text>
				</div>
			);
		}
	}

	return (
		<div
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={{
				__html: DOMPurify.sanitize(fieldHtml, {
					ALLOWED_TAGS: ['p', 'strong', 'span', 'a'],
					ALLOWED_ATTR: ['data-issue-key', 'href'],
				}),
			}}
		/>
	);
};

export default FieldRenderer;
